import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { differenceInDays } from 'date-fns';
import { LAYOUT_HEIGHT, DIVERT_RETURN_STATUS, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { removeItemByValueImmutable, filterFlights } from 'state/helpers';
import { 
    countValuesByFieldAndDuration,
    countFlightsByFieldAndDuration, 
} from './utils';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "dailyFlightsByDep", label: "by Port" },
  { value: "dailyFlightsBySector", label: "by Sector" },
  { value: "dailyFlightsBySectorType", label: "by Sector Type" },
  { value: "dailyFlightsByFlightType", label: "by Flight Type" },
  { value: "dailyFlightsByFlightReg", label: "by Flight Reg" },
  { value: "dailyFlightsByACType", label: "by AC Type" },
  { value: "dailyFlightsByCountry", label: "by Country" },
];

const DailyFlightChart = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    
    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);    
    const [selectedChart, setSelectedChart] = useState(null);

    const [view, setView] = useState('dailyFlightsByDep');
    const [flights, setFlights] = useState([]);     
    
    const flightsData = useSelector((state) => state.flights); 

    useEffect(() => {        
        const handleFlights = async () => {
            try {           
                setLoading(true); 
                const processedFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                    .filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                    );
                
                setFlights(processedFlights);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]);
        
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);   

    const chartData = useMemo(() => {
        const f = filteredFlights;
        const dateDiff = differenceInDays(endDate, startDate) + 1;

        switch (view) {
            case "dailyFlightsByDep":
                return {
                    dailyFlightsByDep: countValuesByFieldAndDuration(f, "", "dep", dateDiff, "column"),                    
                    dailyBHByDep: countValuesByFieldAndDuration(f, "blockHours", "dep", dateDiff, "column"),     
                    dailyFlightsBHByDep: countFlightsByFieldAndDuration(f, "blockHours", "dep", dateDiff),                     
                };
                
            case "dailyFlightsBySector":
                return {
                    dailyFlightsBySector: countValuesByFieldAndDuration(f, "", "sector", dateDiff, "column"),         
                    dailyBHBySector: countValuesByFieldAndDuration(f, "blockHours", "sector", dateDiff, "column"),         
                    dailyFlightsBHBySector: countFlightsByFieldAndDuration(f, "blockHours", "sector", dateDiff),      
                };

            case "dailyFlightsBySectorType":
                return {
                    dailyFlightsBySectorType: countValuesByFieldAndDuration(f, "", "sectorType", dateDiff, "column"),  
                    dailyBHBySectorType: countValuesByFieldAndDuration(f, "blockHours", "sectorType", dateDiff, "column"),         
                    dailyFlightsBHBySectorType: countFlightsByFieldAndDuration(f, "blockHours", "sectorType", dateDiff),                        
                };

            case "dailyFlightsByFlightType":
                return {
                    dailyFlightsByFlightType: countValuesByFieldAndDuration(f, "", "flightType", dateDiff, "column"),        
                    dailyBHByFlightType: countValuesByFieldAndDuration(f, "blockHours", "flightType", dateDiff, "column"),         
                    dailyFlightsBHByFlightType: countFlightsByFieldAndDuration(f, "blockHours", "flightType", dateDiff),                                    
                };

            case "dailyFlightsByFlightReg":
                return {
                    dailyFlightsByFlightReg: countFlightsByFieldAndDuration(f, "blockHours", "flightReg", dateDiff),                    
                };

            case "dailyFlightsByACType":
                return {
                    // dailyFlightsByACType: countValuesByFieldAndDuration(f, "", "acType", dateDiff, "column"),                    
                    // dailyBHByACType: countValuesByFieldAndDuration(f, "blockHours", "acType", dateDiff, "column"),         
                    dailyFlightsBHByACType: countFlightsByFieldAndDuration(f, "blockHours", "acType", dateDiff),                                    
                    dailyFlightsBHPerFlightRegByACType: countFlightsByFieldAndDuration(f, "blockHours", "acType", dateDiff, { perAircraft: true }),                                    
                };  

            case "dailyFlightsByCountry":
                return {
                    dailyFlightsByCountry: countValuesByFieldAndDuration(f, "", "depCountryName", dateDiff, "column"),                    
                    dailyBHByCountry: countValuesByFieldAndDuration(f, "blockHours", "depCountryName", dateDiff, "column"),         
                    dailyFlightsBHByCountry: countFlightsByFieldAndDuration(f, "blockHours", "depCountryName", dateDiff),                                    
                };
                
            default:
                return [];
        }
    }, [startDate, endDate, view, filteredFlights]);
  
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
                 
    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);
   
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleChartClose = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const chartMap = useMemo(() => {
        const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
        const defaultTitle = `Daily Flight ${period}`;

        return (
            {
                dailyFlightsByDep: [
                    { component: <SortBarChart data={chartData.dailyFlightsByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' digit={1} />, style: {} },
                    { component: <SortBarChart data={chartData.dailyBHByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Hours' digit={1} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsBHByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Value' digit={1} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                    
                ],
                dailyFlightsBySector: [
                    { component: <SortBarChart data={chartData.dailyFlightsBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Flights' digit={1} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
                    { component: <SortBarChart data={chartData.dailyBHBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Hours' digit={1} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsBHBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Value' digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                ],
                dailyFlightsBySectorType: [
                    { component: <SortBarChart data={chartData.dailyFlightsBySectorType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Flights' digit={1} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
                    { component: <SortBarChart data={chartData.dailyBHBySectorType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Hours' digit={1} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsBHBySectorType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Value' digit={1} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                ],
                dailyFlightsByFlightType: [
                    { component: <SortBarChart data={chartData.dailyFlightsByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Flights' digit={1} isHorizontalBottomLegend={!isMobile ? true : false} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} />, style: {} },
                    { component: <SortBarChart data={chartData.dailyBHByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Hours' digit={1} isHorizontalBottomLegend={!isMobile ? true : false} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsBHByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Value' digit={1} isHorizontalBottomLegend={!isMobile ? true : false} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                ],
                dailyFlightsByFlightReg: [
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Value' digit={1} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                ],
                dailyFlightsByACType: [
                    // { component: <SortBarChart data={chartData.dailyFlightsByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={1} />, style: {} },
                    // { component: <SortBarChart data={chartData.dailyBHByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Hours' digit={1} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsBHByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Value' digit={1} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsBHPerFlightRegByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Value' digit={1} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={-50} lineTranslateXOffSet={-5} itemWidthOffSet={15} />, style: {} },                    
                ],
                dailyFlightsByCountry: [
                    { component: <SortBarChart data={chartData.dailyFlightsByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={1} bottomOffSet={45} bottomLegendOffSet={45} />, style: {} },
                    { component: <SortBarChart data={chartData.dailyBHByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Hours' digit={1} bottomOffSet={45} bottomLegendOffSet={45} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.dailyFlightsBHByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Value' digit={1} bottomOffSet={45} bottomLegendOffSet={45} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                ],
            }
        )
    }, [startDate, endDate, isMobile, chartData]);
    
    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts?.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );


    return (
        <Box className="main-container-box">                   
            <FlexBetween>
                <Header 
                    title="Daily Flight" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '8rem', sm: '9rem' } }}
                        >
                            <InputLabel id="daily-flights-view-label">View</InputLabel>
                            <Select
                                labelId="daily-flights-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )} 
            </FlexBetween>            
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"                  
                >
                    {view in chartMap &&
                        renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>                
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleChartClose}
                    chartComponent={selectedChart}
                />
            )}
        </Box>
    );
};

export default DailyFlightChart;
